// Management → Account Managers — one big row per AM with the period's KPIs;
// "Open report" pops the full drill-down (AMReportModal).
import { useCallback, useEffect, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Loader2, RefreshCw, Eye, EyeOff, ChevronRight, Link2 } from "lucide-react";
import AMReportModal from "./am/AMReportModal";
import { gbp, mins, PERIODS } from "./am/amFmt";

function Kpi({ label, value, sub, tone = "text-white", testid }) {
  return (
    <div className="rounded-lg border border-white/[0.07] bg-black/20 px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500 truncate">{label}</div>
      <div className={`text-[17px] font-bold tabular-nums leading-tight ${tone}`} data-testid={testid}>{value}</div>
      {sub && <div className="text-[10.5px] text-slate-400 truncate">{sub}</div>}
    </div>
  );
}

function SentimentBar({ s = {}, total }) {
  if (!total) return <div className="h-1.5 rounded-full bg-white/5" />;
  const seg = [["Positive", "bg-emerald-400"], ["Neutral", "bg-slate-400"], ["Negative", "bg-rose-400"], ["Untagged", "bg-white/10"]];
  return (
    <div className="h-1.5 rounded-full overflow-hidden flex bg-white/5" title={seg.map(([k]) => `${k}: ${s[k] || 0}`).join(" · ")}>
      {seg.map(([k, c]) => <div key={k} className={c} style={{ width: `${((s[k] || 0) / total) * 100}%` }} />)}
    </div>
  );
}

function AMRow({ r, onOpen, onHide }) {
  const q = r.quotes, a = r.activities, t = r.tasks, c = r.calls, p = r.presence;
  const overdue = a.overdue + t.overdue;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/20 transition-colors" data-testid={`am-row-${r.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500/40 to-fuchsia-500/30 grid place-items-center text-white font-bold text-sm shrink-0">{r.name.slice(0, 1)}</div>
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-white truncate" data-testid={`am-name-${r.id}`}>{r.name}</div>
          <div className="text-[11px] text-slate-400 truncate">{r.title || r.email}</div>
        </div>
        {overdue > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-200 text-[10.5px] font-mono"><span className="t2-bang inline-grid place-items-center w-3.5 h-3.5 rounded-full bg-rose-500 text-white text-[9px] font-black">!</span>{overdue} overdue</span>}
        {r.digest && <span className={`text-[10.5px] font-mono ${r.digest === "acknowledged" ? "text-emerald-300" : "text-amber-300"}`}>digest {r.digest}</span>}
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => onHide(r)} title={r.hidden ? "Show in report" : "Hide from report"} data-testid={`am-hide-${r.id}`}
            className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-white">{r.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}</button>
          <button type="button" onClick={() => onOpen(r)} data-testid={`am-open-${r.id}`}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-500/15 border border-orange-400/50 text-orange-200 text-[12px] font-semibold hover:bg-orange-500/25">
            Open report <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="Customers" value={r.customers} sub={`${gbp(r.book_ltv)} book · ${r.unregistered} no trade acct`} testid={`am-kpi-customers-${r.id}`} />
        <Kpi label="Open quotes" value={q.open} sub={gbp(q.open_value)} tone="text-orange-200" />
        <Kpi label="Won" value={q.won} sub={`${gbp(q.won_value)}${q.win_rate != null ? ` · ${q.win_rate}% win` : ""}`} tone="text-emerald-300" testid={`am-kpi-won-${r.id}`} />
        <Kpi label="Paid" value={q.paid} sub={gbp(q.paid_value)} tone="text-emerald-200" />
        <Kpi label="Pipeline moves" value={r.deals.moves} sub={`${r.deals.deal_moves} deal · ${r.deals.lead_moves} lead · ${r.deals.won} won`} tone="text-sky-200" />
        <Kpi label="Activities" value={`${a.done} done`} sub={`${a.open} open · ${a.overdue} overdue`} tone={a.overdue ? "text-rose-200" : "text-white"} />
        <div className="rounded-lg border border-white/[0.07] bg-black/20 px-3 py-2 min-w-0">
          <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500">Calls</div>
          <div className="text-[17px] font-bold tabular-nums leading-tight text-white" data-testid={`am-kpi-calls-${r.id}`}>{c.total}</div>
          <div className="text-[10.5px] text-slate-400 mb-1">{c.avg_score != null ? `score ${c.avg_score}` : "no scores"}{c.positive_pct != null ? ` · ${c.positive_pct}% +ve` : ""}</div>
          <SentimentBar s={c.sentiment} total={c.total} />
        </div>
        <Kpi label="Online" value={mins(p.online_mins)} sub={p.online_mins ? `${p.active_pct}% active · today ${mins(p.today_mins)}` : "no tracked time yet"} tone="text-fuchsia-200" testid={`am-kpi-online-${r.id}`} />
      </div>
    </div>
  );
}

export default function AMReports() {
  const [period, setPeriod] = useState({ days: 30 });
  const [showHidden, setShowHidden] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [openAm, setOpenAm] = useState(null);

  const qs = period.start ? `start=${period.start}&end=${period.end || ""}` : `days=${period.days}`;
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try { const { data: d } = await api.get(`/am-reports/overview?${qs}&include_hidden=${showHidden}`); setData(d); }
    catch (e) { setErr(e?.response?.data?.detail || "Could not load report"); }
    finally { setLoading(false); }
  }, [qs, showHidden]);
  useEffect(() => { load(); }, [load]);

  const toggleHide = async (r) => { await api.patch(`/am-reports/${r.id}/settings`, { hidden: !r.hidden }); load(); };
  const T = data?.totals || {};

  return (
    <Layout>
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="am-reports-page">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-fuchsia-300/80">Management</div>
            <h1 className="text-2xl font-display font-bold text-white">Account Managers</h1>
            <p className="text-[12px] text-slate-400">Performance, pipeline, calls and time online for every account manager.</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {PERIODS.map(([d, l]) => (
              <button key={d} type="button" onClick={() => setPeriod({ days: d })} data-testid={`am-period-${d}`}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${!period.start && period.days === d ? "bg-orange-500/15 border-orange-400/50 text-orange-200" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>
            ))}
            <input type="date" value={period.start || ""} onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))} data-testid="am-period-start"
              className="px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-slate-200" />
            <input type="date" value={period.end || ""} onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))} data-testid="am-period-end"
              className="px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[12px] text-slate-200" />
            <button type="button" onClick={() => setShowHidden((v) => !v)} data-testid="am-show-hidden"
              className={`px-3 py-1.5 rounded-full text-[12px] border ${showHidden ? "border-fuchsia-400/50 text-fuchsia-200" : "border-white/10 text-slate-400"}`}>{showHidden ? "Showing hidden" : `Hidden (${data?.hidden?.length || 0})`}</button>
            <button type="button" onClick={load} data-testid="am-refresh" className="p-2 rounded-lg border border-white/10 text-slate-300"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
          </div>
        </div>

        {err && <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 px-4 py-3 text-sm" data-testid="am-error">{err}</div>}
        {!data && !err && <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Gathering every account manager…</div>}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="am-totals">
              <Kpi label="Account managers" value={data.rows.length} tone="text-white" />
              <Kpi label="Won (period)" value={gbp(T.won_value)} sub={`${T.won} quotes`} tone="text-emerald-300" testid="am-total-won" />
              <Kpi label="Paid (period)" value={gbp(T.paid_value)} tone="text-emerald-200" />
              <Kpi label="Calls" value={T.calls} tone="text-sky-200" />
              <Kpi label="Overdue items" value={T.overdue} tone={T.overdue ? "text-rose-300" : "text-white"} />
              <Kpi label="Team time online" value={mins(T.online_mins)} tone="text-fuchsia-200" />
            </div>
            {data.unmatched_call_names?.length > 0 && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-2.5 text-[12px] text-amber-100 flex items-center gap-2 flex-wrap" data-testid="am-unmatched">
                <Link2 className="w-4 h-4 text-amber-300" /> Calls not linked to anyone yet:
                {data.unmatched_call_names.map((n) => <span key={n} className="px-2 py-0.5 rounded-full border border-amber-500/40 font-mono text-[11px]">{n}</span>)}
                <span className="text-amber-300/70">— open an account manager's report → Settings to link them.</span>
              </div>
            )}
            <div className="space-y-3">
              {data.rows.map((r) => <AMRow key={r.id} r={r} onOpen={setOpenAm} onHide={toggleHide} />)}
              {data.rows.length === 0 && <div className="text-center text-slate-500 py-16">No account managers with activity in this period.</div>}
            </div>
          </>
        )}
      </div>
      {openAm && <AMReportModal am={openAm} qs={qs} unmatched={data?.unmatched_call_names || []} onClose={() => setOpenAm(null)} onChanged={load} />}
    </Layout>
  );
}
